import type {
  EnvironmentId,
  PullRequestAction,
  PullRequestDetail,
  PullRequestMergeMethod,
  PullRequestRef,
  PullRequestUpdateMethod,
} from "@cmmd-forge/contracts";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useRef, useState, type RefObject } from "react";

import { cmmdApiClient } from "~/environments/cmmdApi";
import {
  pullRequestActivityQueryOptions,
  pullRequestCommandMutationOptions,
  pullRequestDetailQueryOptions,
  pullRequestStatsQueryOptions,
} from "~/lib/pullRequestReactQuery";
import { pullRequestQueryKeys } from "~/lib/pullRequestQueryKeys";
import {
  allowedPullRequestMergeMethods,
  allowedPullRequestUpdateMethods,
  findPullRequestStat,
  selectedPullRequestMethod,
  visiblePullRequestActions,
} from "./pullRequestDetailPanel.logic";

export function usePullRequestDetailPanelModel(input: {
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
}) {
  const queryClient = useQueryClient();
  const detailQuery = useQuery(pullRequestDetailQueryOptions(input));
  const activityQuery = useQuery(pullRequestActivityQueryOptions(input));
  const statsQuery = useQuery(
    pullRequestStatsQueryOptions({ environmentId: input.environmentId, refs: [input.reference] }),
  );
  const pendingAction = useRef<PendingAction>(null);
  const pendingUpdate = useRef<PendingUpdate>(null);
  const [mergeMethod, setMergeMethod] = useState<PullRequestMergeMethod>("merge");
  const [updateMethod, setUpdateMethod] = useState<PullRequestUpdateMethod>("merge");
  const actionMutation = usePullRequestActionMutation(input, pendingAction);
  const updateMutation = usePullRequestUpdateMutation(input, pendingUpdate);
  const detail = detailQuery.data;
  const isDetailFresh = pullRequestDetailIsAuthoritative({
    isSuccess: detailQuery.isSuccess,
    isRefetchError: detailQuery.isRefetchError,
  });
  const derived = derivePullRequestDetailModel(detail, isDetailFresh);
  const { mergeMethods, updateMethods, actions } = derived;
  const selectedMergeMethod = selectedPullRequestMethod(mergeMethod, mergeMethods, "merge");
  const selectedUpdateMethod = selectedPullRequestMethod(updateMethod, updateMethods, "merge");
  return {
    detail,
    detailQuery,
    activityQuery,
    stat: findPullRequestStat(statsQuery.data?.stats, input.reference),
    isDetailFresh,
    mergeMethods,
    updateMethods,
    selectedMergeMethod,
    selectedUpdateMethod,
    actions,
    setMergeMethod,
    setUpdateMethod,
    actionMutation,
    updateMutation,
    refreshPullRequest: () => refreshPullRequestQueries(queryClient, input.environmentId),
    runAction: (action: PullRequestAction) => {
      pendingAction.current = actionPayload(action, selectedMergeMethod, selectedUpdateMethod);
      actionMutation.mutate();
    },
    updatePullRequest: async (update: NonNullable<PendingUpdate>) => {
      pendingUpdate.current = update;
      await updateMutation.mutateAsync();
    },
  };
}

export async function refreshPullRequestQueries(
  queryClient: Pick<QueryClient, "refetchQueries">,
  environmentId: EnvironmentId,
): Promise<void> {
  await queryClient.refetchQueries({
    queryKey: pullRequestQueryKeys.all(environmentId),
    type: "active",
  });
}

export function pullRequestDetailIsAuthoritative(input: {
  readonly isSuccess: boolean;
  readonly isRefetchError: boolean;
}) {
  return input.isSuccess && !input.isRefetchError;
}

function derivePullRequestDetailModel(detail: PullRequestDetail | undefined, isFresh: boolean) {
  if (detail === undefined) return { mergeMethods: [], updateMethods: [], actions: [] };
  const mergeMethods = allowedPullRequestMergeMethods(detail);
  const updateMethods = allowedPullRequestUpdateMethods(detail);
  return {
    mergeMethods,
    updateMethods,
    actions: visiblePullRequestActions({ detail, isFresh, mergeMethods, updateMethods }),
  };
}

type PendingAction =
  | (Readonly<{
      action: PullRequestAction;
      mergeMethod?: PullRequestMergeMethod;
      updateMethod?: PullRequestUpdateMethod;
    }> &
      object)
  | null;
type PendingUpdate = { readonly title?: string; readonly body?: string } | null;

function actionPayload(
  action: PullRequestAction,
  mergeMethod: PullRequestMergeMethod,
  updateMethod: PullRequestUpdateMethod,
) {
  if (action === "merge" || action === "enable-auto-merge") return { action, mergeMethod };
  if (action === "update-branch") return { action, updateMethod };
  return { action };
}

function usePullRequestActionMutation(
  input: { readonly environmentId: EnvironmentId; readonly reference: PullRequestRef },
  pending: RefObject<PendingAction>,
) {
  const queryClient = useQueryClient();
  return useMutation(
    pullRequestCommandMutationOptions({
      ...input,
      queryClient,
      command: async () => {
        const action = pending.current;
        if (action === null) throw new Error("No pull request action was selected.");
        if (input.reference.projectRepositoryId === undefined)
          throw new Error("This pull request has no canonical Command repository identity.");
        await cmmdApiClient.runPullRequestAction({
          projectId: Number(input.reference.projectId),
          projectRepositoryId: input.reference.projectRepositoryId,
          number: input.reference.number,
          idempotencyKey: crypto.randomUUID(),
          ...action,
        });
      },
    }),
  );
}

function usePullRequestUpdateMutation(
  input: { readonly environmentId: EnvironmentId; readonly reference: PullRequestRef },
  pending: RefObject<PendingUpdate>,
) {
  const queryClient = useQueryClient();
  return useMutation(
    pullRequestCommandMutationOptions({
      ...input,
      queryClient,
      command: async () => {
        const update = pending.current;
        if (update === null) throw new Error("No pull request update was selected.");
        if (input.reference.projectRepositoryId === undefined)
          throw new Error("This pull request has no canonical Command repository identity.");
        await cmmdApiClient.updatePullRequest({
          projectId: Number(input.reference.projectId),
          projectRepositoryId: input.reference.projectRepositoryId,
          number: input.reference.number,
          idempotencyKey: crypto.randomUUID(),
          ...update,
        });
      },
    }),
  );
}
